import { HtmlFinalStep, HtmlStepperStep } from '@superfunnel/types';
import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { PREVIEW_ROOT } from '../../utils';

/** Delay after syncing form data for React to re-render (ms) */
const SYNC_SETTLE_DELAY = 100;

/** Delay after sending websiteUpdated before starting captures (ms) */
const INITIAL_SETTLE_DELAY = 500;

/** Debounce before processing the update queue (ms) */
const UPDATE_QUEUE_DEBOUNCE = 400;

/**
 * Timeout for waiting for a stepChanged ack after goToStep (ms).
 * If the step was already shown (same-step recapture), no ack fires,
 * so we fall back to this timeout.
 */
const STEP_CHANGE_TIMEOUT = 300;

/** Special key used to signal a "wrapper" update → recapture all steps */
const WRAPPER_KEY = '__wrapper__';

interface CaptureRequest {
  stepId: string;
  stepType: 'step' | 'finalStep';
}

export interface ThumbnailHarvesterState {
  /** Cached HTML snapshots keyed by `${stepType}:${stepId}` */
  cache: Map<string, string>;
  /** Whether the harvester is currently capturing */
  harvesting: boolean;
  /** A version counter that increments whenever the cache updates */
  cacheVersion: number;
}

interface UseThumbnailHarvesterProps {
  /** The iframe src URL for the preview */
  iframeSrc: string | null;
  /** The form object (sent as websiteUpdated) */
  form: any;
  /** The website object */
  website: any;
  /** The selected form ID */
  selectedFormId: string;
  /** The quiz ID (forms are nested under quizzes in the website data) */
  quizId: string;
  /** List of regular steps */
  steps: HtmlStepperStep[];
  /** List of final steps (only HTML type — redirect steps don't need thumbnails) */
  finalSteps: HtmlFinalStep[];
}

function makeCacheKey(stepType: string, stepId: string) {
  return `${stepType}:${stepId}`;
}

/**
 * Manages a single hidden "harvester" iframe that captures static HTML
 * snapshots of each step. On initial load, captures all steps. After that,
 * callers use `requestStepUpdate` to queue specific steps for re-capture.
 * Wrapper updates trigger a full re-capture of all steps (since the wrapper
 * wraps every step's HTML).
 */
export function useThumbnailHarvester({
  iframeSrc,
  form,
  website,
  selectedFormId,
  quizId,
  steps,
  finalSteps,
}: UseThumbnailHarvesterProps): ThumbnailHarvesterState & {
  iframeRef: RefObject<HTMLIFrameElement | null>;
  requestStepUpdate: (
    stepId: string,
    stepType: 'step' | 'finalStep' | 'wrapper',
  ) => void;
} {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [cache, setCache] = useState<Map<string, string>>(new Map());
  const [harvesting, setHarvesting] = useState(false);
  const [cacheVersion, setCacheVersion] = useState(0);

  // Track whether the iframe is ready
  const isReadyRef = useRef(false);
  // Whether the initial full harvest has completed
  const initialHarvestDoneRef = useRef(false);
  // Abort controller for cancelling in-flight operations
  const abortRef = useRef<AbortController | null>(null);
  // Pending message resolver (for waiting on htmlCaptured responses)
  const pendingResolveRef = useRef<((html: string) => void) | null>(null);
  // Pending resolver for step navigation acknowledgment
  const stepChangedResolveRef = useRef<(() => void) | null>(null);
  // Queue of pending step updates (deduped by cache key)
  const updateQueueRef = useRef<Set<string>>(new Set());
  // Timer for debounced queue processing
  const queueTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Whether the queue processor is currently running
  const processingRef = useRef(false);
  // Latest form/website refs (so the queue processor always has fresh data)
  const formRef = useRef(form);
  formRef.current = form;
  const websiteRef = useRef(website);
  websiteRef.current = website;
  const selectedFormIdRef = useRef(selectedFormId);
  selectedFormIdRef.current = selectedFormId;
  const quizIdRef = useRef(quizId);
  quizIdRef.current = quizId;
  const stepsRef = useRef(steps);
  stepsRef.current = steps;
  const finalStepsRef = useRef(finalSteps);
  finalStepsRef.current = finalSteps;

  // -- Send a postMessage to the hidden iframe --
  const sendMessage = useCallback((message: Record<string, unknown>) => {
    if (!iframeRef.current?.contentWindow) return;
    const payload = JSON.stringify(message);
    iframeRef.current.contentWindow.postMessage(payload, PREVIEW_ROOT);
  }, []);

  // -- Listen for messages from the hidden iframe --
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.origin !== PREVIEW_ROOT) return;
      if (event.source !== iframeRef.current?.contentWindow) return;

      try {
        const parsed = JSON.parse(event.data);

        if (parsed.eventName === 'ready') {
          isReadyRef.current = true;
        }

        // Step navigation acknowledgment from the stepper form
        if (parsed.eventName === 'stepChanged') {
          if (stepChangedResolveRef.current) {
            stepChangedResolveRef.current();
            stepChangedResolveRef.current = null;
          }
        }

        if (parsed.eventName === 'htmlCaptured' || parsed.data?.requestId) {
          const data =
            parsed.eventName === 'htmlCaptured' ? parsed.data : parsed;
          if (data.requestId && pendingResolveRef.current) {
            pendingResolveRef.current(data.html || '');
            pendingResolveRef.current = null;
          }
        }
      } catch {
        // ignore
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  // -- Wait for the iframe "ready" signal --
  const waitForReady = useCallback((signal: AbortSignal): Promise<void> => {
    if (isReadyRef.current) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const check = setInterval(() => {
        if (signal.aborted) {
          clearInterval(check);
          reject(new DOMException('Aborted', 'AbortError'));
        }
        if (isReadyRef.current) {
          clearInterval(check);
          resolve();
        }
      }, 100);
    });
  }, []);

  /**
   * Wait for the stepper form to acknowledge that a goToStep navigation
   * has completed and the DOM is showing the correct step.
   *
   * Falls back to a timeout for cases where no ack fires:
   * - Same-step recapture (step was already shown, no state change)
   * - Initial step (step 0 on mount, no navigation needed)
   */
  const waitForStepChanged = useCallback(
    (signal: AbortSignal): Promise<void> => {
      return new Promise((resolve, reject) => {
        if (signal.aborted) {
          reject(new DOMException('Aborted', 'AbortError'));
          return;
        }

        const cleanup = () => {
          stepChangedResolveRef.current = null;
          signal.removeEventListener('abort', onAbort);
          clearTimeout(fallbackTimer);
        };

        const onAbort = () => {
          cleanup();
          reject(new DOMException('Aborted', 'AbortError'));
        };

        signal.addEventListener('abort', onAbort, { once: true });

        stepChangedResolveRef.current = () => {
          cleanup();
          resolve();
        };

        // Fallback: if step was already shown, no ack fires
        const fallbackTimer = setTimeout(() => {
          if (stepChangedResolveRef.current) {
            cleanup();
            resolve();
          }
        }, STEP_CHANGE_TIMEOUT);
      });
    },
    [],
  );

  // -- Request an HTML capture and wait for the response --
  const captureStep = useCallback(
    (
      stepId: string,
      stepType: 'step' | 'finalStep',
      signal: AbortSignal,
    ): Promise<string> => {
      return new Promise((resolve, reject) => {
        if (signal.aborted) {
          reject(new DOMException('Aborted', 'AbortError'));
          return;
        }

        const requestId = `${stepType}:${stepId}:${Date.now()}`;
        pendingResolveRef.current = resolve;

        const onAbort = () => {
          pendingResolveRef.current = null;
          reject(new DOMException('Aborted', 'AbortError'));
        };
        signal.addEventListener('abort', onAbort, { once: true });

        sendMessage({
          eventName: 'captureHtml',
          requestId,
          stepId,
          stepType,
        });

        // Timeout after 5 seconds
        setTimeout(() => {
          if (pendingResolveRef.current === resolve) {
            pendingResolveRef.current = null;
            signal.removeEventListener('abort', onAbort);
            resolve(''); // empty = failed to capture
          }
        }, 5000);
      });
    },
    [sendMessage],
  );

  // -- Build the full capture queue (all steps + final steps) --
  const buildFullQueue = useCallback((): CaptureRequest[] => {
    return [
      ...stepsRef.current.map((s) => ({
        stepId: s.id,
        stepType: 'step' as const,
      })),
      ...finalStepsRef.current
        .filter((s) => s.stepType !== 'redirect')
        .map((s) => ({ stepId: s.id, stepType: 'finalStep' as const })),
    ];
  }, []);

  // -- Send websiteUpdated to sync form data to the harvester iframe --
  const syncFormData = useCallback(
    (signal: AbortSignal): Promise<void> => {
      const currentFormId = selectedFormIdRef.current;
      const currentQuizId = quizIdRef.current;
      const currentForm = formRef.current;

      sendMessage({
        eventName: 'websiteUpdated',
        formId: currentFormId,
        website: {
          ...websiteRef.current?.development,
          forms: {
            [currentFormId]: currentForm,
          },
          quizzes: {
            [currentQuizId]: {
              quizId: currentQuizId,
              formIdsWeights: { [currentFormId]: 100 },
              forms: { [currentFormId]: currentForm },
            },
          },
        },
      });
      return delay(SYNC_SETTLE_DELAY, signal);
    },
    [sendMessage],
  );

  // -- Capture a list of steps sequentially --
  const captureQueue = useCallback(
    async (queue: CaptureRequest[], signal: AbortSignal) => {
      for (const req of queue) {
        if (signal.aborted) break;

        // Navigate to the step
        sendMessage({
          eventName: 'goToStep',
          currentStep: { type: req.stepType, id: req.stepId },
        });

        // Wait for stepper to acknowledge the step change.
        // If the step was already shown (same-step), the ack won't fire
        // and we'll fall back to the STEP_CHANGE_TIMEOUT.
        await waitForStepChanged(signal);
        if (signal.aborted) break;

        // Capture
        const html = await captureStep(req.stepId, req.stepType, signal);
        if (html && !signal.aborted) {
          const key = makeCacheKey(req.stepType, req.stepId);
          setCache((prev) => {
            const next = new Map(prev);
            next.set(key, html);
            return next;
          });
          setCacheVersion((v) => v + 1);
        }
      }
    },
    [sendMessage, captureStep, waitForStepChanged],
  );

  // -- Run the initial full harvest (all steps) --
  const runInitialHarvest = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const { signal } = controller;

    if (!iframeSrc || !formRef.current || !selectedFormIdRef.current) return;

    setHarvesting(true);
    isReadyRef.current = false;

    try {
      await waitForReady(signal);
      syncFormData(signal);
      await delay(INITIAL_SETTLE_DELAY, signal);

      const queue = buildFullQueue();
      await captureQueue(queue, signal);

      if (!signal.aborted) {
        initialHarvestDoneRef.current = true;
      }
    } catch (e: unknown) {
      if (e instanceof DOMException && e.name === 'AbortError') return;
      console.error('[ThumbnailHarvester] initial harvest error', e);
    } finally {
      if (!signal.aborted) {
        setHarvesting(false);
      }
    }
  }, [iframeSrc, waitForReady, syncFormData, buildFullQueue, captureQueue]);

  // -- Process the update queue --
  const processQueue = useCallback(async () => {
    if (processingRef.current) return;
    if (updateQueueRef.current.size === 0) return;
    if (!initialHarvestDoneRef.current) return;

    processingRef.current = true;
    setHarvesting(true);

    // Cancel any existing operation (previous queue run)
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const { signal } = controller;

    try {
      // Drain: snapshot the queue and clear it
      const keys = new Set(updateQueueRef.current);
      updateQueueRef.current.clear();

      // Wrapper key means recapture everything
      const isFullRecapture = keys.has(WRAPPER_KEY);

      let queue: CaptureRequest[];
      if (isFullRecapture) {
        queue = buildFullQueue();
      } else {
        queue = Array.from(keys).map((key) => {
          const [stepType, stepId] = key.split(':') as [
            'step' | 'finalStep',
            string,
          ];
          return { stepId, stepType };
        });
      }

      if (queue.length === 0) return;

      // Wait for iframe ready (should already be ready post-initial)
      await waitForReady(signal);

      // Sync latest form data
      await syncFormData(signal);

      // Capture
      await captureQueue(queue, signal);

      // If more items were queued while we were processing, schedule another run
      if (updateQueueRef.current.size > 0 && !signal.aborted) {
        queueTimerRef.current = setTimeout(processQueue, UPDATE_QUEUE_DEBOUNCE);
      }
    } catch (e: unknown) {
      if (e instanceof DOMException && e.name === 'AbortError') return;
      console.error('[ThumbnailHarvester] queue processing error', e);
    } finally {
      processingRef.current = false;
      if (!controller.signal.aborted) {
        setHarvesting(false);
      }
    }
  }, [waitForReady, syncFormData, buildFullQueue, captureQueue]);

  // -- Public API: request a step to be re-captured --
  const requestStepUpdate = useCallback(
    (stepId: string, stepType: 'step' | 'finalStep' | 'wrapper') => {
      if (!initialHarvestDoneRef.current) return; // still in initial harvest

      if (stepType === 'wrapper') {
        // Wrapper affects all steps — add special key
        updateQueueRef.current.add(WRAPPER_KEY);
      } else {
        updateQueueRef.current.add(makeCacheKey(stepType, stepId));
      }

      // Debounce: reset the timer each time a new update is requested
      if (queueTimerRef.current) {
        clearTimeout(queueTimerRef.current);
      }
      queueTimerRef.current = setTimeout(processQueue, UPDATE_QUEUE_DEBOUNCE);
    },
    [processQueue],
  );

  // -- Initial harvest: run once when iframe src is available --
  useEffect(() => {
    if (!iframeSrc || !form) return;
    // Only run if we haven't done the initial harvest yet
    if (initialHarvestDoneRef.current) return;

    runInitialHarvest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iframeSrc, !!form]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      if (queueTimerRef.current) {
        clearTimeout(queueTimerRef.current);
      }
    };
  }, []);

  return {
    cache,
    harvesting,
    cacheVersion,
    iframeRef,
    requestStepUpdate,
  };
}

function delay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      },
      { once: true },
    );
  });
}
